import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// import axios from "axios"; // 👈 SUPPRIMER si plus utilisé
import { Network } from "vis-network";
import { DataSet } from "vis-data";
import apiClient from '../utils/apiClient';
import publicApiClient from '../utils/publicApiClient';
import { getNodeColor } from '../utils/featureTeamColors'; // 👈 NEW: Import color utility
import { getTestsCount } from '../utils/testUtils'; // 🧪 Import test counter


const GraphView = ({
  nodes,
  relationships,
  onNodeSelect,
  selectedNode,
  colorMode = 'type',        // 👈 NEW: Color mode prop
  selectedTeams = [],        // 👈 NEW: Selected teams for filtering
  showLegend = true,        // 👈 NEW: Show legend prop
  legendInFilter = false,   // 👈 NEW: If true, legend is rendered in filter panel and should be hidden here
  onNodeEdit, // Ajouté pour la modale d'édition
  onNodeDelete, // Ajouté pour la suppression
  onRelationshipEdit,
  onRelationshipDelete,
  onGraphRefresh,
  onOpenSidePanel, // Nouvelle prop pour ouvrir le side panel
  onOpenTutorialModal, // 👈 NEW: Callback pour ouvrir le modal tutoriel
}) => {
  const { t } = useTranslation();
  const graphRef = useRef(null);
  const networkRef = useRef(null);
  const nodePositionsRef = useRef({}); // 🚀 Utiliser une ref au lieu de state pour éviter les re-renders
  const isFirstRender = useRef(true);
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [allNodes, setAllNodes] = useState([]);

  // Nouveaux états pour le menu contextuel
  const [contextMenu, setContextMenu] = useState({
    type: null, // 'node', 'edge' ou 'canvas'
    id: null,
    position: { x: 0, y: 0 },
    isOpen: false,
  });

  // Nouveaux états pour la recherche
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedNodes, setHighlightedNodes] = useState([]);

  // 🚀 OPTIMISATION: Mémoïser la fonction hasTests
  const hasTests = useCallback((node) => {
    return getTestsCount(node.testsJson) > 0;
  }, []);

  // 🚀 OPTIMISATION: Mémoïser getNodeColorByType
  const getNodeColorByType = useCallback((node) => {
    return getNodeColor(node, colorMode);
  }, [colorMode]);

  // 🎨 Fonction pour trouver les composantes connexes (clusters de nœuds reliés)
  const findConnectedComponents = useCallback((nodes, edges) => {
    const components = [];
    const visited = new Set();
    
    // Créer une map d'adjacence
    const adjacency = new Map();
    nodes.forEach(node => adjacency.set(node.id, []));
    
    edges.forEach(edge => {
      if (adjacency.has(edge.from)) adjacency.get(edge.from).push(edge.to);
      if (adjacency.has(edge.to)) adjacency.get(edge.to).push(edge.from);
    });
    
    // Parcours en profondeur pour trouver chaque composante
    const dfs = (nodeId, component) => {
      visited.add(nodeId);
      component.push(nodeId);
      
      const neighbors = adjacency.get(nodeId) || [];
      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          dfs(neighbor, component);
        }
      });
    };
    
    // Trouver toutes les composantes
    nodes.forEach(node => {
      if (!visited.has(node.id)) {
        const component = [];
        dfs(node.id, component);
        components.push(component);
      }
    });
    
    return components;
  }, []);

  // 📍 Fonction pour positionner les clusters en cercle initialement
  const positionClustersInCircle = useCallback((nodes, edges) => {
    const components = findConnectedComponents(nodes, edges);
    const clusterPositions = {};
    
    // Rayon du cercle basé sur le nombre de clusters
    const numClusters = components.length;
    const radius = Math.max(500, numClusters * 120);
    
    components.forEach((component, clusterIndex) => {
      // Position du centre du cluster sur le cercle
      const angle = (2 * Math.PI * clusterIndex) / numClusters;
      const clusterCenterX = radius * Math.cos(angle);
      const clusterCenterY = radius * Math.sin(angle);
      
      // Positionner les nœuds du cluster autour de ce centre
      const clusterRadius = Math.max(180, component.length * 35);
      component.forEach((nodeId, nodeIndex) => {
        const nodeAngle = (2 * Math.PI * nodeIndex) / component.length;
        clusterPositions[nodeId] = {
          x: clusterCenterX + clusterRadius * Math.cos(nodeAngle),
          y: clusterCenterY + clusterRadius * Math.sin(nodeAngle)
        };
      });
    });
    
    return clusterPositions;
  }, [findConnectedComponents]);

  // ⚠️ Compter les nœuds isolés (composantes connexes de taille 1)
  const isolatedNodeCount = useMemo(() => {
    if (!graphData.nodes.length) return 0;
    const components = findConnectedComponents(graphData.nodes, graphData.edges);
    return components.filter(c => c.length === 1).length;
  }, [graphData, findConnectedComponents]);

  // — list of isolated node IDs for click-to-select
  const isolatedNodeIds = useMemo(() => {
    if (!graphData.nodes.length) return [];
    const components = findConnectedComponents(graphData.nodes, graphData.edges);
    return components.filter(c => c.length === 1).flat();
  }, [graphData, findConnectedComponents]);

  // 🎨 Générer les configurations de groupes basées sur les composantes connexes
  const groupConfigs = useMemo(() => {
    const colors = [
      { bg: 'rgba(99, 102, 241, 0.12)', border: '#6366f1' },   // Indigo
      { bg: 'rgba(16, 185, 129, 0.12)', border: '#10b981' },   // Emerald
      { bg: 'rgba(245, 158, 11, 0.12)', border: '#f59e0b' },   // Amber
      { bg: 'rgba(239, 68, 68, 0.12)', border: '#ef4444' },    // Red
      { bg: 'rgba(168, 85, 247, 0.12)', border: '#a855f7' },   // Purple
      { bg: 'rgba(59, 130, 246, 0.12)', border: '#3b82f6' },   // Blue
      { bg: 'rgba(236, 72, 153, 0.12)', border: '#ec4899' },   // Pink
      { bg: 'rgba(20, 184, 166, 0.12)', border: '#14b8a6' },   // Teal
      { bg: 'rgba(251, 146, 60, 0.12)', border: '#fb923c' },   // Orange
      { bg: 'rgba(132, 204, 22, 0.12)', border: '#84cc16' },   // Lime
    ];
    
    return colors;
  }, []);

  // 🎨 Fonction pour dessiner des zones de fond colorées derrière les groupes
  const drawGroupBackgrounds = useCallback((ctx, network, visNodes, visEdges) => {
    if (!ctx || !network) return;

    const nodePositions = network.getPositions();
    
    // Trouver les composantes connexes
    const nodesArray = visNodes.get();
    const edgesArray = visEdges.get();
    const components = findConnectedComponents(nodesArray, edgesArray);
    
    // Grouper les nœuds par composante
    const nodesByGroup = components.map(component => {
      return component.map(nodeId => {
        const pos = nodePositions[nodeId];
        if (pos) {
          return {
            id: nodeId,
            x: pos.x,
            y: pos.y
          };
        }
        return null;
      }).filter(Boolean);
    });

    // Dessiner un rectangle arrondi avec padding pour chaque composante
    nodesByGroup.forEach((groupNodes, index) => {
      if (groupNodes.length === 0) return;

      // Calculer les bornes du groupe (bounding box) en coordonnées réseau
      const basePadding = groupNodes.length === 1 ? 80 : 60;
      const padding = basePadding;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      groupNodes.forEach(node => {
        const nodeWidth = 100;
        const nodeHeight = 50;
        minX = Math.min(minX, node.x - nodeWidth);
        minY = Math.min(minY, node.y - nodeHeight);
        maxX = Math.max(maxX, node.x + nodeWidth);
        maxY = Math.max(maxY, node.y + nodeHeight);
      });

      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Obtenir la couleur du groupe (rotation des couleurs)
      const colorConfig = groupConfigs[index % groupConfigs.length];
      if (!colorConfig) return;

      // Dessiner le rectangle avec bordure arrondie
      const borderRadius = 20;
      ctx.save();
      
      // Fond coloré semi-transparent
      ctx.fillStyle = colorConfig.bg;
      ctx.beginPath();
      ctx.moveTo(minX + borderRadius, minY);
      ctx.lineTo(maxX - borderRadius, minY);
      ctx.quadraticCurveTo(maxX, minY, maxX, minY + borderRadius);
      ctx.lineTo(maxX, maxY - borderRadius);
      ctx.quadraticCurveTo(maxX, maxY, maxX - borderRadius, maxY);
      ctx.lineTo(minX + borderRadius, maxY);
      ctx.quadraticCurveTo(minX, maxY, minX, maxY - borderRadius);
      ctx.lineTo(minX, minY + borderRadius);
      ctx.quadraticCurveTo(minX, minY, minX + borderRadius, minY);
      ctx.closePath();
      ctx.fill();

      // Bordure solide
      ctx.strokeStyle = colorConfig.border;
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.85;
      ctx.stroke();

      // Label du groupe (en haut à gauche avec badge)
      ctx.globalAlpha = 1;
      ctx.fillStyle = colorConfig.border;
      ctx.font = 'bold 13px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      const labelText = `Groupe ${index + 1} (${groupNodes.length})`;
      const textWidth = ctx.measureText(labelText).width;
      
      // Badge background
      ctx.fillStyle = colorConfig.bg;
      const badgePadding = 10;
      const badgeHeight = 22;
      ctx.fillRect(
        minX + 15 - badgePadding/2, 
        minY + 10, 
        textWidth + badgePadding, 
        badgeHeight
      );
      
      // Badge border
      ctx.strokeStyle = colorConfig.border;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 1;
      ctx.strokeRect(
        minX + 15 - badgePadding/2, 
        minY + 10, 
        textWidth + badgePadding, 
        badgeHeight
      );
      
      // Text
      ctx.fillStyle = colorConfig.border;
      ctx.fillText(labelText, minX + 15, minY + 26);

      ctx.restore();
    });
  }, [groupConfigs, findConnectedComponents]);

  // 🚀 OPTIMISATION: Fonction pour charger les données du graphe (sans dépendance sur selectedNode)
  const loadGraphData = useCallback(() => {
    setContextMenu({
      type: null,
      id: null,
      position: { x: 0, y: 0 },
      isOpen: false,
    });
    setLoading(true);
    Promise.all([
      apiClient.get('/nodes'),
      apiClient.get('/relationships'),
    ])
      .then(([nodesResponse, relationsResponse]) => {
        const allNodesData = nodesResponse.data;
        setAllNodes(allNodesData);

        // Filter nodes by selected teams if filtering is active
        const filteredNodesData = selectedTeams.length > 0 
          ? allNodesData.filter(node => {
              const nodeTeam = node.featureTeam || 'No Team';
              return selectedTeams.includes(nodeTeam);
            })
          : allNodesData;

        // Préparer les edges pour le calcul de clusters
        const visibleNodeNames = new Set(filteredNodesData.map(n => n.name));
        const filteredEdgesData = relationsResponse.data
          .filter(rel => visibleNodeNames.has(rel.nodeName1) && visibleNodeNames.has(rel.nodeName2))
          .map(rel => ({ from: rel.nodeName1, to: rel.nodeName2 }));

        // 📍 Calculer les positions initiales des clusters en cercle
        const clusterPositions = positionClustersInCircle(
          filteredNodesData.map(n => ({ id: n.name })),
          filteredEdgesData
        );

        const nodes = filteredNodesData.map((node) => {
          const nodeHasTests = hasTests(node);
          
          // Utiliser les positions sauvegardées OU les positions de cluster OU laisser vis-network décider
          const savedPos = nodePositionsRef.current[node.name];
          const clusterPos = clusterPositions[node.name];
          
          return {
            id: node.name,
            label: nodeHasTests ? `🧪 ${node.name}` : node.name,
            title: nodeHasTests 
              ? `${node.description || node.name}\n${t('graphView.tooltips.hasTests')}`
              : node.description || node.name,
            type: node.type,
            color: getNodeColorByType(node),
            borderWidth: 2,
            font: { size: 16, face: 'Inter, sans-serif' },
            shadow: nodeHasTests ? { enabled: true, color: "#10b981", size: 12, x: 3, y: 3  } : false,
            ...(savedPos ? savedPos : clusterPos),
          };
        });

        // ✨ Si aucun nœud : créer un nœud fantôme de bienvenue
        if (nodes.length === 0) {
          nodes.push({
            id: '__welcome__',
            label: '👋 ' + t('graphView.emptyState.welcome', 'Bienvenue !'),
            title: t('graphView.emptyState.tooltip', 'Commencez par créer votre premier nœud'),
            color: {
              background: '#7c3aed',
              border: '#6d28d9',
              highlight: {
                background: '#8b5cf6',
                border: '#7c3aed'
              }
            },
            shape: 'box',
            borderWidth: 3,
            borderWidthSelected: 5,
            font: { 
              size: 18, 
              color: '#ffffff',
              bold: true
            },
            shadow: {
              enabled: true,
              color: 'rgba(124, 58, 237, 0.5)',
              size: 20,
              x: 0,
              y: 0
            },
            x: 0,
            y: 0,
            fixed: { x: true, y: true }
          });
        }

        // Filter edges to only show connections between visible nodes
        const edges = relationsResponse.data
          .filter(rel => visibleNodeNames.has(rel.nodeName1) && visibleNodeNames.has(rel.nodeName2))
          .map((rel) => ({
            from: rel.nodeName1,
            to: rel.nodeName2,
            label: rel.relationshipType,
            font: { 
              size: 16, 
              color: '#475569', 
              strokeWidth: 4, 
              strokeColor: '#ffffff',
              align: 'top',
              bold: true
            },
            arrows: {
              to: { enabled: true, scaleFactor: 1.5, type: 'arrow' },
            },
            width: 13,
            color: { color: '#64748b', highlight: '#3b82f6', hover: '#0ea5e9' },
            smooth: {
              enabled: true,
              type: 'dynamic',
              roundness: 0.5
            }
          }));
          
        setGraphData({ nodes, edges });
        setLoading(false);
      })
      .catch((error) => {
        console.error(t('graphView.errors.loadingData'), error);
        setLoading(false);
      });
  }, [selectedTeams, hasTests, getNodeColorByType, t]); // 🚀 Retiré nodePositions pour éviter les recharges en boucle

  useEffect(() => {
    if (typeof onGraphRefresh === "function") {
      onGraphRefresh(loadGraphData);
    }
    // eslint-disable-next-line
  }, []);

  // 🚀 OPTIMISATION: Mémoïser les options du réseau
  const networkOptions = useMemo(() => ({
    layout: {
      hierarchical: false,
      improvedLayout: true,
    },
    physics: {
      enabled: true,
      solver: "forceAtlas2Based",
      forceAtlas2Based: {
        gravitationalConstant: -180,  // 🚀 Répulsion forte mais pas excessive
        centralGravity: 0.002,         // 🎯 Gravité centrale légère
        springLength: 300,             // 📏 Liens longs entre nœuds
        springConstant: 0.03,          // 🔗 Liens moyennement souples
        damping: 0.65,                 // 🛑 Amortissement pour stabilisation
        avoidOverlap: 0.9              // 🚫 Éviter chevauchement
      },
      minVelocity: 0.75,
      stabilization: {
        enabled: true,
        iterations: 180,               // ⏱️ Itérations optimisées
        updateInterval: 20,
      }
    },
    interaction: {
      hover: true,
      hoverConnectedEdges: true,
      selectConnectedEdges: true,
      tooltipDelay: 200,
      navigationButtons: true,
      keyboard: {
        enabled: true,
        bindToWindow: false
      },
      zoomView: true,
      dragView: true,
      zoomSpeed: 1
    },
    nodes: {
      shape: "box",
      margin: 15,
      widthConstraint: { minimum: 150, maximum: 300 },
      heightConstraint: { minimum: 60 },
      font: {
        size: 18,
        face: 'Inter, -apple-system, sans-serif',
        color: '#1f2937',
        bold: { color: '#111827' }
      },
      borderWidth: 2,
      borderWidthSelected: 4,
      shapeProperties: {
        borderRadius: 6
      },
      chosen: {
        node: (values, id, selected, hovering) => {
          if (hovering || selected) {
            values.borderWidth = 4;
            values.shadow = true;
            values.shadowSize = 15;
            values.shadowX = 3;
            values.shadowY = 3;
            values.shadowColor = 'rgba(0,0,0,0.2)';
          }
        }
      }
    },
    edges: {
      width: 4,
      selectionWidth: 2,
      hoverWidth: 1.5,
      color: { 
        color: '#94a3b8', 
        highlight: '#3b82f6', 
        hover: '#0ea5e9',
        inherit: false
      },
      smooth: {
        enabled: true,
        type: "dynamic",
        roundness: 0.5,
        forceDirection: 'none'
      },
      font: { 
        size: 16, 
        align: 'top',
        color: '#475569',
        strokeWidth: 4, 
        strokeColor: '#ffffff',
        bold: true
      },
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 1.5
        }
      },
      chosen: {
        edge: (values, id, selected, hovering) => {
          if (hovering || selected) {
            values.width = 6;
            values.color = '#3b82f6';
          }
        },
        label: (values, id, selected, hovering) => {
          if (hovering || selected) {
            values.size = 18;
            values.color = '#1e40af';
          }
        }
      }
    },
    groups: groupConfigs, // 🎨 Appliquer les configurations de groupes
  }), [groupConfigs]);

  // Initialiser ou mettre à jour le réseau lorsque les données changent
  useEffect(() => {
    if (!graphRef.current || graphData.nodes.length === 0) return;

    // Si le réseau existe déjà, le détruire proprement
    if (networkRef.current) {
      try {
        networkRef.current.destroy();
      } catch (err) {
        console.warn('Error destroying previous network:', err);
      }
      networkRef.current = null;
    }

    // 🚀 OPTIMISATION: Pas de délai, créer immédiatement pour un affichage plus rapide
    // Créer des DataSets pour vis-network
    const visNodes = new DataSet(graphData.nodes);
    const visEdges = new DataSet(graphData.edges);
    const visData = { nodes: visNodes, edges: visEdges };
    
    // Créer un nouveau réseau
    const network = new Network(graphRef.current, visData, networkOptions);

    // 🎨 Dessiner les zones de fond colorées pour chaque groupe AVANT le dessin du réseau
    network.on('beforeDrawing', (ctx) => {
      drawGroupBackgrounds(ctx, network, visNodes, visEdges);
    });

      // Ajouter l'événement de clic sur un nœud
      network.on("click", function (params) {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          
          // ✨ Cas spécial : nœud de bienvenue
          if (nodeId === '__welcome__') {
            if (onNodeSelect) {
              onNodeSelect({ name: '__welcome__' });
            }
            return;
          }
          
          // Utilise les données locales
          const fullNode = allNodes.find((node) => node.name === nodeId);
          if (fullNode && onNodeSelect) {
            onNodeSelect(fullNode);
          }
        }
      });

      // Gestionnaire de clic droit pour éditer nœuds et relations
      network.on("oncontext", function (params) {
        console.log("Clic droit détecté!", params);
        params.event.preventDefault();

        // Obtenir le nœud ou la relation sous le curseur
        const nodeId = network.getNodeAt(params.pointer.DOM);
        const edgeId = network.getEdgeAt(params.pointer.DOM);

        console.log("Node at click:", nodeId);
        console.log("Edge at click:", edgeId);

        // Clic droit sur un nœud
        if (nodeId) {
          setContextMenu({
            type: "node",
            id: nodeId,
            position: params.pointer.DOM,
            isOpen: true,
          });
          console.log("Menu contextuel ouvert pour:", nodeId);
        }
        // Clic droit sur une relation
        else if (edgeId) {
          setContextMenu({
            type: "edge",
            id: edgeId,
            position: params.pointer.DOM,
            isOpen: true,
          });
        }
        // Clic droit sur le fond (pour créer un nouveau nœud)
        else {
          setContextMenu({
            type: "canvas",
            position: params.pointer.DOM,
            isOpen: true,
          });
        }
      });

    // Stocker l'instance dans la ref
    networkRef.current = network;

    // 🚀 OPTIMISATION: Désactiver la physique après stabilisation pour meilleures performances
    network.once("stabilized", function () {
      const positions = network.getPositions();
      nodePositionsRef.current = positions; // 🚀 Utiliser ref au lieu de state
      // Désactiver la physique après la stabilisation initiale
      network.setOptions({ physics: { enabled: false } });
    });

    // Cleanup si le composant se démonte ou si graphData change
    return () => {
      try {
        if (network) {
          network.destroy();
        }
      } catch (err) {
        console.warn('Error during network cleanup:', err);
      }
      // Ne pas réinitialiser networkRef.current ici car c'est géré au début de l'effet
    };
  }, [graphData, allNodes, onNodeSelect, networkOptions, drawGroupBackgrounds]);

  // 🚀 OPTIMISATION: Effet pour mettre en évidence les nœuds - optimisé pour ne mettre à jour que les nœuds modifiés
  useEffect(() => {
    if (!networkRef.current || !networkRef.current.body?.data?.nodes) return;

    // Créer un Set pour un lookup plus rapide
    const highlightedSet = new Set(highlightedNodes);
    const nodesToUpdate = [];

    graphData.nodes.forEach((node) => {
      const fullNode = allNodes.find(n => n.name === node.id);
      const isHighlighted = highlightedSet.has(node.id);
      const isSelected = selectedNode && selectedNode.name === node.id;
      
      let nodeColor;
      let borderWidth = 1;
      
      if (isSelected) {
        nodeColor = { border: "#dd0fb083", highlight: { border: "#ff1744" } };
        borderWidth = 4;
      } else if (isHighlighted) {
        nodeColor = {
          background: "#9e0591ff",
          border: "#ffa000",
        };
        borderWidth = 3;
      } else {
        nodeColor = getNodeColorByType(fullNode || { type: node.type });
      }

      nodesToUpdate.push({
        id: node.id,
        color: nodeColor,
        borderWidth: borderWidth,
        font: isHighlighted ? {
          size: 16,
          color: "#000",
          face: "arial",
          bold: true
        } : {
          size: 14,
          color: "#000",
          face: "arial"
        }
      });
    });

    // 🚀 Batch update pour de meilleures performances
    if (nodesToUpdate.length > 0) {
      networkRef.current.body.data.nodes.update(nodesToUpdate);
    }
  }, [highlightedNodes, selectedNode, graphData.nodes, allNodes, getNodeColorByType]);

  // 🚀 OPTIMISATION: Charger les données initiales
  useEffect(() => {
    loadGraphData();
    // eslint-disable-next-line
  }, []); // Uniquement au montage initial

  // ✨ Sélectionner automatiquement le nœud de bienvenue au chargement si aucun nœud utilisateur
  useEffect(() => {
    if (allNodes.length === 0 && onNodeSelect) {
      // Attendre un peu que le graphe soit initialisé
      setTimeout(() => {
        onNodeSelect({ name: '__welcome__' });
      }, 100);
    }
  }, [allNodes.length, onNodeSelect]);

  // 🚀 OPTIMISATION: Recharger uniquement quand les filtres changent (pas au premier render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadGraphData();
    // eslint-disable-next-line
  }, [selectedTeams, colorMode]); // Recharger uniquement sur changement de filtre

  /*
  // Recharger les données lorsque selectedNode change
  useEffect(() => {
    loadGraphData();
  }, [selectedNode]);
*/
  // Nettoyage lors du démontage du composant
  useEffect(() => {
    return () => {
      setContextMenu({
        type: null,
        id: null,
        position: { x: 0, y: 0 },
        isOpen: false,
      });
      if (networkRef.current) {
        try {
          networkRef.current.destroy();
        } catch (err) {
          console.warn('Error during final network cleanup:', err);
        }
        networkRef.current = null;
      }
    };
  }, []);

  // Fonctions de gestion des actions du menu contextuel
  const handleEditNode = (nodeId) => {
    console.log("Édition demandée pour le nœud:", nodeId);

    apiClient.get('/nodes')
      .then((response) => {
        const node = response.data.find((n) => n.name === nodeId);
        if (node && onOpenSidePanel) {
          onOpenSidePanel('node-edit', node);
        }
      })
      .catch((error) =>
        console.error(t('graphView.errors.nodeRetrieve'), error)
      );

    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleDeleteNode = (nodeId) => {
    if (
      window.confirm(t('graphView.confirmations.deleteNode', { nodeId }))
    ) {
      apiClient // 👈 CORRECTION : utiliser apiClient
        .delete(`/node/${nodeId}`)
        .then(() => {
          loadGraphData(); // Recharger les données
        })
        .catch((error) =>
          console.error(t('graphView.errors.nodeDelete'), error)
        );
    }

    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleEditEdge = (edgeId) => {
    // Edge IDs can be numbers generated by vis.js
    // Find the relationship in graphData
    const edge = graphData.edges.find((e) => e.id === edgeId);

    if (edge && onOpenSidePanel) {
      // Prepare edge data for side panel
      const edgeData = {
        id: edge.id,
        from: edge.from,
        to: edge.to,
        relationshipType: edge.label,
        properties: edge.properties || {}
      };
      onOpenSidePanel('edge-edit', edgeData);
    } else {
      console.error(t('graphView.errors.relationNotFound'), edgeId);
    }

    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleDeleteRelationFromNode = (nodeId) => {
    // Trouve toutes les relations liées à ce nœud
    const relatedEdges = graphData.edges.filter(
      (e) => e.from === nodeId || e.to === nodeId
    );
    if (relatedEdges.length === 0) {
      toast.warn(t('graphView.alerts.noRelationToDelete'));
      return;
    }
    // Demande à l'utilisateur quelle relation supprimer
    const relLabels = relatedEdges.map(
      (e, i) => `${i + 1}. ${e.from} --[${e.label}]--> ${e.to}`
    );
    const choice = prompt(
      t('graphView.prompts.selectRelationToDelete', { relations: relLabels.join("\n") })
    );
    const idx = parseInt(choice, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= relatedEdges.length) return;
    const edge = relatedEdges[idx];
    if (
      window.confirm(
        t('graphView.confirmations.deleteRelation', { from: edge.from, label: edge.label, to: edge.to })
      )
    ) {
      apiClient // 👈 CORRECTION : utiliser apiClient
        .delete(`/node/relationship`, {
          data: {
            nodeName1: edge.from,
            nodeName2: edge.to,
            relationshipType: edge.label,
          },
        })
        .then(() => {
          loadGraphData();
        })
        .catch((error) =>
          console.error(t('graphView.errors.relationDelete'), error)
        );
    }
    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleDeleteEdge = (edgeId) => {
    const edge = graphData.edges.find((e) => e.id === edgeId);
    if (
      edge &&
      window.confirm(t('graphView.confirmations.deleteRelationGeneric'))
    ) {
      apiClient // 👈 CORRECTION : utiliser apiClient
        .delete(`/node/relationship`, {
          data: {
            nodeName1: edge.from,
            nodeName2: edge.to,
            relationshipType: edge.label,
          },
        })
        .then(() => {
          loadGraphData();
        })
        .catch((error) =>
          console.error(t('graphView.errors.relationDelete'), error)
        );
    }

    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleCreateNode = () => {
    if (onNodeSelect) onNodeSelect(null); // Vide la sélection AVANT d'ouvrir le side panel
    if (onOpenSidePanel) {
      onOpenSidePanel('node-create');
    }
    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleCreateEdge = (sourceNodeId) => {
    if (onOpenSidePanel) {
      // Préparer les données initiales pour la création d'edge
      const edgeData = {
        from: sourceNodeId,
        to: '',
        relationshipType: '',
        properties: {}
      };
      onOpenSidePanel('edge-create', edgeData);
    }
    setContextMenu({ ...contextMenu, isOpen: false });
  };

  // 🚀 OPTIMISATION: Mémoïser la fonction de recherche
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setHighlightedNodes([]);
      return;
    }

    const queryLower = query.toLowerCase();
    const matches = nodes.filter(node =>
      node.name.toLowerCase().includes(queryLower) ||
      (node.description && node.description.toLowerCase().includes(queryLower)) ||
      node.type.toLowerCase().includes(queryLower)
    ).map(node => node.name);

    setHighlightedNodes(matches);

    // Focus sur le premier nœud trouvé
    if (matches.length > 0 && networkRef.current) {
      networkRef.current.focus(matches[0], { scale: 1.2, animation: true });
    }
  }, [nodes]);

  // Fonction de recherche vectorielle (gardée pour compatibilité)
  /*
  const handleVectorSearch = async (query) => {
    if (query.length < 3) {
      setHighlightedNodes([]);
      return;
    }

    try {
      const results = await publicApiClient.post('/vector-optional/search', {
        query,
        limit: 5,
      });

      // Extraire les noms des nœuds trouvés
      const foundNodeNames = results.data
        .filter((item) => item.type === "node")
        .map((item) => item.name);

      setHighlightedNodes(foundNodeNames);

      // Mettre en évidence les nœuds trouvés dans le graphe existant
      if (foundNodeNames.length > 0 && networkRef.current) {
        // Modification douce des couleurs des nœuds
        const updatedNodes = graphData.nodes.map((node) => {
          if (foundNodeNames.includes(node.name)) {
            return {
              ...node,
              color: {
                background: "#ffeb3b",
                border: "#ffa000",
                highlight: { background: "#ffeb3b", border: "#ff6f00" },
              },
              borderWidth: 3,
            };
          }
          return node;
        });

        // Mettre à jour uniquement l'apparence
        networkRef.current.setData({
          nodes: updatedNodes,
          edges: networkRef.current.body.data.edges,
        });
      }
    } catch (error) {
      console.error(t('graphView.errors.search'), error);
    }
  };
*/
  // Composant de menu contextuel
  const ContextMenu = ({
    menu,
    onClose,
    onEditNode,
    onDeleteNode,
    onEditEdge,
    onDeleteEdge,
    onCreateNode,
    onCreateEdge,
    onDeleteRelationFromNode,
  }) => {
    if (!menu.isOpen) return null;

    const style = {
      position: "absolute",
      top: `${menu.position.y}px`,
      left: `${menu.position.x}px`,
      zIndex: 1000,
      backgroundColor: "white",
      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
      borderRadius: "4px",
      overflow: "hidden",
    };

    const menuItemStyle = {
      padding: "8px 12px",
      cursor: "pointer",
      borderBottom: "1px solid #eee",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    };

    // Fermer le menu si on clique ailleurs
    const handleClickOutside = (e) => {
      if (e.target.closest(".context-menu") === null) {
        onClose();
      }
    };

    React.useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div
        className="context-menu"
        style={{
          ...style,
          border: "2px solid red", // Rendre le menu plus visible pour le debug
          minWidth: "150px",
          minHeight: "100px",
        }}
      >
        {menu.type === "node" && menu.id !== '__welcome__' && (
          <>
            <div style={menuItemStyle} onClick={() => onEditNode(menu.id)}>
              <span>✏️</span> {t('graphView.contextMenu.editNode')}
            </div>
            <div style={menuItemStyle} onClick={() => onCreateEdge(menu.id)}>
              <span>🔗</span> {t('graphView.createRelation')}
            </div>
            <div style={menuItemStyle} onClick={() => onDeleteNode(menu.id)}>
              <span>🗑️</span> {t('graphView.contextMenu.deleteNode')}
            </div>
            <div
              style={menuItemStyle}
              onClick={() => onDeleteRelationFromNode(menu.id)}
            >
              <span>🗑️</span> {t('graphView.contextMenu.deleteRelation')}
            </div>
          </>
        )}
        {menu.type === "edge" && (
          <>
            <div style={menuItemStyle} onClick={() => onEditEdge(menu.id)}>
              <span>✏️</span> {t('graphView.contextMenu.editRelation')}
            </div>
            <div style={menuItemStyle} onClick={() => onDeleteEdge(menu.id)}>
              <span>🗑️</span> {t('graphView.contextMenu.deleteRelationAction')}
            </div>
          </>
        )}
        {menu.type === "canvas" && (
          <div
            style={menuItemStyle}
            onClick={() => {
              onNodeSelect(null); // Vide la sélection
              setTimeout(() => {
                onCreateNode();
              }, 0);
            }}
          >
            <span>➕</span> {t('graphView.contextMenu.createNode')}
          </div>
        )}
      </div>
    );
  };

  // Modale d'édition de nœud
  const NodeEditModal = ({
    node,
    onClose,
    onSave,
    position = { x: 100, y: 100 },
    graphRef,
  }) => {
    const [formData, setFormData] = useState({
      name: node.name || "",
      type: node.type || "",
      description: node.description || "",
      technology: node.technology || "",
      status: node.status || "Nouveau",
      metadata: JSON.stringify(node.metadata || {}, null, 2),
      endpointSchema: JSON.stringify(node.endpointSchema || {}, null, 2),
    });

    const modalRef = useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
      if (modalRef.current && graphRef?.current) {
        const modalRect = modalRef.current.getBoundingClientRect();
        const graphRect = graphRef.current.getBoundingClientRect();

        let relX = position.x - graphRect.left + 10;
        let relY = position.y - graphRect.top + 10;
        const maxX = graphRect.width - modalRect.width - 10;
        const maxY = graphRect.height - modalRect.height - 10;
        relX = Math.max(10, Math.min(relX, maxX));
        relY = Math.max(10, Math.min(relY, maxY));
        setAdjustedPosition({ x: relX, y: relY });
      }
    }, [position, graphRef]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      let metadataObj = {};
      let endpointSchemaObj = {};
      try {
        metadataObj = formData.metadata ? JSON.parse(formData.metadata) : {};
      } catch {
        toast.error(t('graphView.alerts.invalidMetadataJson'));
        return;
      }
      try {
        endpointSchemaObj = formData.endpointSchema
          ? JSON.parse(formData.endpointSchema)
          : {};
      } catch {
        toast.error(t('graphView.alerts.invalidEndpointSchemaJson'));
        return;
      }
      onSave({
        ...formData,
        metadata: metadataObj,
        endpointSchema: endpointSchemaObj,
      });
    };

    return (
      <div
        ref={modalRef}
        className="modal-content"
        style={{
          position: "absolute",
          left: adjustedPosition.x,
          top: adjustedPosition.y,
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflow: "auto",
          zIndex: 1001,
        }}
      >
        <h2>{t('graphView.modals.editNode.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Type:</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="">Sélectionner un type</option>
              <option value="Feature">Feature</option>
              <option value="Feature Team">Feature Team</option>
              <option value="Component">Component</option>
              <option value="API">API</option>
              <option value="Service">Service</option>
              <option value="User">User</option>
              <option value="Module">Module</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description:</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Technologie:</label>
            <input
              type="text"
              name="technology"
              value={formData.technology}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Statut:</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Nouveau">{t('graphView.new')}</option>
              <option value="En cours">En cours</option>
              <option value="Terminé">{t('graphView.statuses.completed')}</option>
              <option value="Archivé">{t('graphView.statuses.archived')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>Métadonnées (JSON):</label>
            <textarea
              name="metadata"
              value={formData.metadata}
              onChange={handleChange}
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Schéma des Endpoints (JSON):</label>
            <textarea
              name="endpointSchema"
              value={formData.endpointSchema}
              onChange={handleChange}
              rows="2"
            />
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Annuler
            </button>
            <button type="submit" className="save-button">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Modale de création de nœud
  const NodeCreateModal = ({ onClose, onSave, position, graphRef }) => {
    const [formData, setFormData] = useState({
      name: "",
      type: "",
      description: "",
      technology: "",
      status: "Nouveau",
      metadata: "{}", // Saisie JSON
      endpointSchema: "{}", // Saisie JSON
    });

    const modalRef = useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
      if (modalRef.current && graphRef.current) {
        const modalRect = modalRef.current.getBoundingClientRect();
        const graphRect = graphRef.current.getBoundingClientRect();

        // Position du clic relative au conteneur
        let relX = position.x - graphRect.left;
        let relY = position.y - graphRect.top;

        // Offset pour ne pas coller au bord
        relX += 10;
        relY += 10;

        // Contraindre dans le conteneur
        const maxX = graphRect.width - modalRect.width - 10;
        const maxY = graphRect.height - modalRect.height - 10;
        relX = Math.max(10, Math.min(relX, maxX));
        relY = Math.max(10, Math.min(relY, maxY));

        setAdjustedPosition({ x: relX, y: relY });
      }
    }, [position, graphRef]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      let metadataObj = {};
      let endpointSchemaObj = {};
      try {
        metadataObj = formData.metadata ? JSON.parse(formData.metadata) : {};
      } catch {
        toast.error(t('graphView.alerts.invalidMetadataJson'));
        return;
      }
      try {
        endpointSchemaObj = formData.endpointSchema
          ? JSON.parse(formData.endpointSchema)
          : {};
      } catch {
        toast.error(t('graphView.alerts.invalidEndpointSchemaJson'));
        return;
      }
      onSave({
        ...formData,
        metadata: metadataObj,
        endpointSchema: endpointSchemaObj,
      });
    };

    return (
      <div
        ref={modalRef}
        className="modal-content"
        style={{
          position: "absolute",
          left: adjustedPosition.x,
          top: adjustedPosition.y,
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflow: "auto",
          zIndex: 1001, // au-dessus du graph et du menu contextuel
        }}
      >
        <h2>{t('graphView.createNewNode')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Type:</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="">Sélectionner un type</option>
              <option value="Feature">Feature</option>
              <option value="Feature Team">Feature Team</option>
              <option value="Component">Component</option>
              <option value="API">API</option>
              <option value="Service">Service</option>
              <option value="User">User</option>
              <option value="Module">Module</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description:</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Technologie:</label>
            <input
              type="text"
              name="technology"
              value={formData.technology}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Métadonnées (JSON):</label>
            <textarea
              name="metadata"
              value={formData.metadata}
              onChange={handleChange}
              rows="2"
              placeholder='{"exemple": "valeur"}'
            />
          </div>
          <div className="form-group">
            <label>Schéma des Endpoints (JSON):</label>
            <textarea
              name="endpointSchema"
              value={formData.endpointSchema}
              onChange={handleChange}
              rows="2"
              placeholder='{"endpoint": "/api/xyz"}'
            />
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Annuler
            </button>
            <button type="submit" className="save-button">
              Créer
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Modale de création de relation
  const EdgeCreateModal = ({
    sourceNodeId,
    onClose,
    onSave,
    allNodes,
    position = { x: 100, y: 100 },
    graphRef,
  }) => {
    const [formData, setFormData] = useState({
      source: sourceNodeId,
      target: "",
      type: "USES",
    });

    const modalRef = useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
      if (modalRef.current && graphRef?.current) {
        const modalRect = modalRef.current.getBoundingClientRect();
        const graphRect = graphRef.current.getBoundingClientRect();

        let relX = position.x - graphRect.left + 10;
        let relY = position.y - graphRect.top + 10;
        const maxX = graphRect.width - modalRect.width - 10;
        const maxY = graphRect.height - modalRect.height - 10;
        relX = Math.max(10, Math.min(relX, maxX));
        relY = Math.max(10, Math.min(relY, maxY));
        setAdjustedPosition({ x: relX, y: relY });
      }
    }, [position, graphRef]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div
        ref={modalRef}
        className="modal-content"
        style={{
          position: "absolute",
          left: adjustedPosition.x,
          top: adjustedPosition.y,
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflow: "auto",
          zIndex: 1001, // au-dessus du graph et du menu contextuel
        }}
      >
        <h2>{t('graphView.createRelation')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Depuis:</label>
            <input type="text" value={sourceNodeId} disabled />
          </div>
          <div className="form-group">
            <label>Vers:</label>
            <select
              name="target"
              value={formData.target}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionner un nœud cible</option>
              {allNodes
                .filter((node) => node.name !== sourceNodeId)
                .map((node) => (
                  <option key={node.name} value={node.name}>
                    {node.name} ({node.type})
                  </option>
                ))}
            </select>
          </div>
          <div className="form-group">
            <label>Type de relation:</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="USES">USES</option>
              <option value="CALLS">CALLS</option>
              <option value="DEPENDS_ON">DEPENDS_ON</option>
              <option value="IMPLEMENTS">IMPLEMENTS</option>
              <option value="CONTAINS">CONTAINS</option>
              <option value="RELATED_TO">RELATED_TO</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Annuler
            </button>
            <button type="submit" className="save-button">
              Créer
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Modale d'édition de relation
  const EdgeEditModal = ({
    edge,
    onClose,
    onSave,
    position = { x: 100, y: 100 },
    graphRef,
  }) => {
    const [formData, setFormData] = useState({
      source: edge.from,
      target: edge.to,
      type: edge.label,
    });

    const modalRef = useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
      if (modalRef.current && graphRef?.current) {
        const modalRect = modalRef.current.getBoundingClientRect();
        const graphRect = graphRef.current.getBoundingClientRect();

        let relX = position.x - graphRect.left + 10;
        let relY = position.y - graphRect.top + 10;
        const maxX = graphRect.width - modalRect.width - 10;
        const maxY = graphRect.height - modalRect.height - 10;
        relX = Math.max(10, Math.min(relX, maxX));
        relY = Math.max(10, Math.min(relY, maxY));
        setAdjustedPosition({ x: relX, y: relY });
      }
    }, [position, graphRef]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div
        ref={modalRef}
        className="modal-content"
        style={{
          position: "absolute",
          left: adjustedPosition.x,
          top: adjustedPosition.y,
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflow: "auto",
          zIndex: 1001, // au-dessus du graph et du menu contextuel
        }}
      >
        <h2>{t('graphView.editRelation')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Depuis:</label>
            <input type="text" value={formData.source} disabled />
          </div>
          <div className="form-group">
            <label>Vers:</label>
            <input type="text" value={formData.target} disabled />
          </div>
          <div className="form-group">
            <label>Type de relation:</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="USES">USES</option>
              <option value="CALLS">CALLS</option>
              <option value="DEPENDS_ON">DEPENDS_ON</option>
              <option value="IMPLEMENTS">IMPLEMENTS</option>
              <option value="CONTAINS">CONTAINS</option>
              <option value="RELATED_TO">RELATED_TO</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Annuler
            </button>
            <button type="submit" className="save-button">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    );
  };

  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      // Entrer en mode plein écran
      try {
        await graphRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Erreur lors de l\'activation du plein écran:', err);
      }
    } else {
      // Sortir du mode plein écran
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Erreur lors de la désactivation du plein écran:', err);
      }
    }
  };

  // Écouter les changements de mode plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div>
      {selectedNode && (
        <div className="mb-4 p-3 rounded bg-gray-50 border border-gray-200">
          <strong>{t('graphView.selectedNode')}:</strong>{" "}
          <span className="font-semibold">{selectedNode.name}</span>
          <div className="text-sm text-gray-600">
            <br />
          </div>
        </div>
      )}
      <div className="flex gap-2 mb-2">
        <button
          onClick={loadGraphData}
          disabled={loading}
          className="refresh-button"
        >
          {loading ? t('graphView.loading') : t('graphView.refreshGraph')}
        </button>
        <button
          onClick={() => {
            if (networkRef.current) {
              networkRef.current.fit({
                animation: { duration: 500, easingFunction: "easeInOutQuad" },
              });
            }
          }}
          className="center-button"
        >
          {t('graphView.buttons.centerGraph')}
        </button>
        <button
          onClick={() => {
            if (networkRef.current && selectedNode && selectedNode.name) {
              networkRef.current.focus(selectedNode.name, {
                scale: 1.5,
                animation: { duration: 500, easingFunction: "easeInOutQuad" },
              });
            }
          }}
          className="zoom-button"
          disabled={!selectedNode}
        >
          {t('graphView.buttons.zoomToNode')}
        </button>
        <button
          onClick={() => {
            if (onNodeSelect) onNodeSelect(null);
            localStorage.removeItem("llmScenariosNode");
          }}
          className="deselect-button"
          disabled={!selectedNode}
        >
          {t('graphView.buttons.deselectNode')}
        </button>
        <button
          onClick={() => {
            if (onNodeSelect) onNodeSelect(null); // Désélectionne tout
            setTimeout(() => {
              if (onOpenSidePanel) {
                onOpenSidePanel('node-create');
              }
            }, 0);
          }}
          className="create-node-button bg-green-600 text-white px-3 py-1 rounded"
        >
          {t('graphView.buttons.createNode')}
        </button>
        <button
          onClick={toggleFullscreen}
          className={`fullscreen-button ${isFullscreen ? 'active' : ''}`}
        >
          <span className="icon" style={{ marginRight: '8px' }}>
            {isFullscreen ? '🔳' : '📺'}
          </span>
          {isFullscreen ? t('graphView.exitFullscreen') : t('enterFullscreen')}
        </button>
        <button
          onClick={onOpenTutorialModal}
          className="tutorial-video-button"
          title={t('graphView.watchTutorial', 'Watch Tutorial')}
        >
          <span className="icon" style={{ marginRight: '8px' }}>
            🎥
          </span>
          {t('graphView.tutorial', 'Tutorial')}
        </button>
      </div>

      {/* Overlay de recherche */}
      <div className="absolute top-2 right-2 z-10 w-64">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2">
          <input
            type="text"
            placeholder={t('nodes.searchNodes') || 'Rechercher des nœuds...'}
            className="w-full p-2 rounded border border-gray-300 text-sm"
            value={searchQuery}
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
          />
          {searchQuery && highlightedNodes.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto">
              <div className="text-xs text-gray-500 p-1">
                {highlightedNodes.length} résultat(s)
              </div>
              {highlightedNodes.map((nodeName) => (
                <div
                  key={nodeName}
                  className="p-1 hover:bg-blue-50 cursor-pointer text-sm border-b"
                  onClick={() => {
                    // Focus sur ce nœud
                    const nodeObj = nodes.find((n) => n.name === nodeName);
                    if (nodeObj) onNodeSelect(nodeObj);
                    // Focus sur le nœud dans le graphe
                    if (networkRef.current)
                      networkRef.current.focus(nodeName, {
                        scale: 1.3,
                        animation: true,
                      });
                    // Fermer la liste des résultats
                    setSearchQuery("");
                    setHighlightedNodes([]);
                  }}
                >
                  {nodeName}
                </div>
              ))}
            </div>
          )}
          {searchQuery && highlightedNodes.length === 0 && (
            <div className="mt-2 text-xs text-gray-500 p-1">
              {t('nodes.noResultsFound')}
            </div>
          )}
        </div>
      </div>

      <div
        className={`graph-container ${isFullscreen ? 'fullscreen' : ''}`}
        ref={graphRef}
        style={{
          height: isFullscreen ? '100vh' : '500px',
          width: '100%',
          position: 'relative',
          backgroundColor: isFullscreen ? '#fff' : 'transparent',
        }}
      >
        {/* Boutons de contrôle en mode plein écran */}
        {isFullscreen && (
          <div className="fullscreen-controls" style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 1002,
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <button
              onClick={() => {
                if (networkRef.current) {
                  networkRef.current.fit({
                    animation: { duration: 500, easingFunction: "easeInOutQuad" },
                  });
                }
              }}
              className="center-button"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              {t('graphView.buttons.centerGraph')}
            </button>
            <button
              onClick={() => {
                if (networkRef.current && selectedNode && selectedNode.name) {
                  networkRef.current.focus(selectedNode.name, {
                    scale: 1.5,
                    animation: { duration: 500, easingFunction: "easeInOutQuad" },
                  });
                }
              }}
              className="zoom-button"
              disabled={!selectedNode}
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              {t('graphView.buttons.zoomToNode')}
            </button>
            <button
              onClick={toggleFullscreen}
              className="fullscreen-button active"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              🚪{t('exitFullScreen')}
            </button>
          </div>
        )}

        {/* Barre de recherche repositionnée en mode plein écran */}
        {isFullscreen && (
          <div className="fullscreen-search" style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1002,
            width: '280px'
          }}>
            <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2">
              <input
                type="text"
                placeholder={t('nodes.searchNodes') || 'Rechercher des nœuds...'}
                className="w-full p-2 rounded border border-gray-300 text-sm"
                value={searchQuery}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
              />
              {searchQuery && highlightedNodes.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto">
                  <div className="text-xs text-gray-500 p-1">
                    {highlightedNodes.length} résultat(s)
                  </div>
                  {highlightedNodes.map((nodeName) => (
                    <div
                      key={nodeName}
                      className="p-1 hover:bg-blue-50 cursor-pointer text-sm border-b"
                      onClick={() => {
                        const nodeObj = nodes.find((n) => n.name === nodeName);
                        if (nodeObj) onNodeSelect(nodeObj);
                        if (networkRef.current)
                          networkRef.current.focus(nodeName, {
                            scale: 1.3,
                            animation: true,
                          });
                        setSearchQuery("");
                        setHighlightedNodes([]);
                      }}
                    >
                      {nodeName}
                    </div>
                  ))}
                </div>
              )}
              {searchQuery && highlightedNodes.length === 0 && (
                <div className="mt-2 text-xs text-gray-500 p-1">
                  {t('nodes.noResultsFound')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Menu contextuel */}
        <ContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
          onEditNode={handleEditNode}
          onDeleteNode={handleDeleteNode}
          onEditEdge={handleEditEdge}
          onDeleteEdge={handleDeleteEdge}
          onCreateNode={handleCreateNode}
          onCreateEdge={handleCreateEdge}
          onDeleteRelationFromNode={handleDeleteRelationFromNode}
        />

        {/* ⚠️ Nœuds isolés — badge flottant */}
        {isolatedNodeCount > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '14px',
            left: '14px',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '7px 14px',
            background: 'rgba(245, 158, 11, 0.92)',
            color: 'white',
            borderRadius: '20px',
            fontSize: '0.78rem',
            fontWeight: '600',
            boxShadow: '0 2px 14px rgba(245, 158, 11, 0.45)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'auto',
            cursor: 'default',
            userSelect: 'none',
          }}>
            <span>⚠️ {isolatedNodeCount} nœud{isolatedNodeCount > 1 ? 's' : ''} isolé{isolatedNodeCount > 1 ? 's' : ''}</span>
            <button
              onClick={() => {
                const nodeId = isolatedNodeIds[0];
                if (!nodeId) return;
                const nodeObj = nodes.find(n => (n.id || n.name) === nodeId);
                if (nodeObj && onNodeSelect) onNodeSelect(nodeObj);
                if (networkRef.current) networkRef.current.focus(nodeId, { scale: 1.4, animation: true });
              }}
              title="Sélectionner le premier nœud isolé"
              style={{
                background: 'white',
                color: '#d97706',
                border: 'none',
                borderRadius: '12px',
                padding: '3px 10px',
                fontSize: '0.72rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
            >
              Connecter →
            </button>
          </div>
        )}
      </div>

      {/* Légende des couleurs des types de nœuds (rendu uniquement si la légende n'est pas fournie dans le panneau de filtres) */}
      {!legendInFilter && (
        <div
          className="mt-4 items-center"
          style={{
            display: "flex",
            marginTop: "10px",
            fontWeight: "bold",
            flexDirection: "row",
            justifyContent: "space-evenly",
          }}
        >
          <div className="font-semibold">{t('graphView.legend.title')} :</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#f078d6",
                border: "2px solid rgb(241, 32, 196)",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.featureTeam')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#4CAF50",
                border: "2px solid #2E7D32",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.feature')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#2196F3",
                border: "2px solid #1565C0",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.component')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#FFC107",
                border: "2px solid #FFA000",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.api')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#9C27B0",
                border: "2px solid #7B1FA2",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.service')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#F44336",
                border: "2px solid #C62828",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.user')}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "#3F51B5",
                border: "2px solid #303F9F",
                borderRadius: 4,
                marginRight: 4,
              }}
            />
            {t('graphView.legend.types.module')}
          </div>
        </div>
      )}

      {/* 🎨 Légende des groupes (Feature Teams) */}
      {!legendInFilter && Object.keys(groupConfigs).length > 0 && (
        <div
          className="mt-4 items-center"
          style={{
            display: "flex",
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "1px solid #e5e7eb",
            fontWeight: "bold",
            flexDirection: "row",
            justifyContent: "flex-start",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div className="font-semibold" style={{ width: "100%", marginBottom: "8px" }}>
            🎨 {t('graphView.legend.groups', 'Groupes (Feature Teams)')}:
          </div>
          {Object.entries(groupConfigs).map(([groupName, config]) => (
            <div key={groupName} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 20,
                  background: config.color.background,
                  border: `2px ${config.shapeProperties?.borderDashes ? 'dashed' : 'solid'} ${config.color.border}`,
                  borderRadius: 6,
                }}
              />
              <span style={{ fontSize: "13px", color: config.color.border, fontWeight: 600 }}>
                {groupName}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GraphView;
